import { db } from '../firebase/config'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  enableNetwork,
  disableNetwork,
  waitForPendingWrites,
  writeBatch
} from 'firebase/firestore'
import { withRetry, getFirebaseErrorMessage } from '../firebase/errorHandler'
import { useUserStore } from '../../stores/userStore'
import MediaService from '../media/MediaService'
import { v4 as uuidv4 } from 'uuid'
import { targetedCloneForMutation } from '@/utils/cloneHelpers'
import { hasPlayer, getPlayerRole } from '@/utils/campaignPlayers'
import {
  buildCharacterCampaignPreview,
  resolveCharacterCampaignIds
} from '@/utils/characterCampaignPreview'
import CampaignCharacterPreviewService, {
  buildCampaignCharacterPreviewDoc,
  getCampaignCharacterPreviewDocRef
} from '@/services/campaign/CampaignCharacterPreviewService'

/**
 * Recursively sanitize an object to remove undefined values
 * Firestore doesn't accept undefined, so we convert to null or remove the field
 * @param {any} obj - Object to sanitize
 * @param {boolean} removeUndefined - If true, remove undefined fields; if false, convert to null
 * @returns {any} - Sanitized object
 */
function sanitizeUndefinedValues(obj, removeUndefined = true) {
  if (obj === null || obj === undefined) {
    return removeUndefined ? undefined : null
  }

  if (Array.isArray(obj)) {
    return obj
      .map((item) => sanitizeUndefinedValues(item, removeUndefined))
      .filter((item) => item !== undefined)
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const sanitized = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = sanitizeUndefinedValues(obj[key], removeUndefined)
        if (value !== undefined) {
          sanitized[key] = value
        } else if (!removeUndefined) {
          sanitized[key] = null
        }
        // If removeUndefined is true and value is undefined, skip adding it
      }
    }
    return sanitized
  }

  return obj
}

const PUBLIC_CHARACTER_PERMISSION_CASES = ['PUBLIC', 'public', 'Public']
const PUBLIC_CHARACTERS_CACHE_KEY = 'public_characters_discover_v1'
const PUBLIC_CHARACTERS_CACHE_TTL_MS = 5 * 60 * 1000

let publicCharactersMemoryCache = null
let publicCharactersPendingRequest = null

function readCharacterPermission(record) {
  return String(record?.permissions || record?.frontmatter?.permissions || 'PRIVATE').toUpperCase()
}

function toComparableTimestamp(value) {
  if (!value) return 0
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? 0 : parsed
  }
  if (value instanceof Date) return value.getTime()
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.seconds === 'number') return value.seconds * 1000
  return 0
}

function comparePublicCharacterRecords(a, b) {
  const bTime = toComparableTimestamp(b?.updatedAt || b?.updated || b?.createdAt)
  const aTime = toComparableTimestamp(a?.updatedAt || a?.updated || a?.createdAt)
  if (bTime !== aTime) return bTime - aTime
  return String(a?.name || '').localeCompare(String(b?.name || ''))
}

function normalizePublicCharacterRecord(record) {
  const cloned = targetedCloneForMutation(record || {})
  return {
    ...cloned,
    ownerId: cloned.ownerId || cloned.owner || cloned.userId || cloned.createdBy || null,
    permissions: readCharacterPermission(cloned),
    campaignId: cloned.campaignId || cloned.frontmatter?.campaignId || null,
    allowedUserIds: Array.isArray(cloned.allowedUserIds)
      ? [...cloned.allowedUserIds]
      : Array.isArray(cloned.frontmatter?.allowedUserIds)
        ? [...cloned.frontmatter.allowedUserIds]
        : []
  }
}

function clonePublicCharacterList(list) {
  return (Array.isArray(list) ? list : []).map((record) => normalizePublicCharacterRecord(record))
}

function isValidPublicCharactersCacheEntry(entry, maxAgeMs = PUBLIC_CHARACTERS_CACHE_TTL_MS) {
  if (!entry || typeof entry.ts !== 'number' || !Array.isArray(entry.data)) return false
  return Date.now() - entry.ts <= maxAgeMs
}

function canUseSessionStorage() {
  try {
    return typeof sessionStorage !== 'undefined'
  } catch {
    return false
  }
}

function readPublicCharactersCacheEntry({
  maxAgeMs = PUBLIC_CHARACTERS_CACHE_TTL_MS,
  allowExpired = false
} = {}) {
  const isUsable = (entry) =>
    !!entry &&
    typeof entry.ts === 'number' &&
    Array.isArray(entry.data) &&
    (allowExpired || isValidPublicCharactersCacheEntry(entry, maxAgeMs))

  if (isUsable(publicCharactersMemoryCache)) {
    return {
      ts: publicCharactersMemoryCache.ts,
      data: clonePublicCharacterList(publicCharactersMemoryCache.data)
    }
  }

  if (!canUseSessionStorage()) return null

  try {
    const raw = sessionStorage.getItem(PUBLIC_CHARACTERS_CACHE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    if (!parsed || !Array.isArray(parsed.data)) {
      sessionStorage.removeItem(PUBLIC_CHARACTERS_CACHE_KEY)
      return null
    }

    if (!allowExpired && !isValidPublicCharactersCacheEntry(parsed, maxAgeMs)) {
      sessionStorage.removeItem(PUBLIC_CHARACTERS_CACHE_KEY)
      return null
    }

    publicCharactersMemoryCache = {
      ts: parsed.ts,
      data: clonePublicCharacterList(parsed.data)
    }

    return {
      ts: publicCharactersMemoryCache.ts,
      data: clonePublicCharacterList(publicCharactersMemoryCache.data)
    }
  } catch {
    try {
      sessionStorage.removeItem(PUBLIC_CHARACTERS_CACHE_KEY)
    } catch {
      /* ignore cache cleanup errors */
    }
    return null
  }
}

function writePublicCharactersCache(list) {
  const entry = {
    ts: Date.now(),
    data: clonePublicCharacterList(list)
  }

  publicCharactersMemoryCache = entry

  if (canUseSessionStorage()) {
    try {
      sessionStorage.setItem(PUBLIC_CHARACTERS_CACHE_KEY, JSON.stringify(entry))
    } catch {
      /* ignore cache persistence failures */
    }
  }

  return clonePublicCharacterList(entry.data)
}

function clearPublicCharactersCacheEntry() {
  publicCharactersMemoryCache = null
  publicCharactersPendingRequest = null

  if (!canUseSessionStorage()) return

  try {
    sessionStorage.removeItem(PUBLIC_CHARACTERS_CACHE_KEY)
  } catch {
    /* ignore cache cleanup failures */
  }
}

async function fetchPublicCharactersFromFirestore() {
  return await withRetry(async () => {
    const charactersRef = collection(db, 'characters')
    const res = []
    const seen = new Set()
    const planErrors = []

    const appendSnapshot = (snapshot) => {
      for (const d of snapshot?.docs || []) {
        if (seen.has(d.id)) continue
        const normalized = normalizePublicCharacterRecord({ id: d.id, ...d.data() })
        if (normalized.permissions !== 'PUBLIC') continue
        seen.add(d.id)
        res.push(normalized)
      }
    }

    const runLegacyFallbackQueries = async (field, label) => {
      let succeeded = false

      for (const value of PUBLIC_CHARACTER_PERMISSION_CASES) {
        try {
          const qFallback = query(charactersRef, where(field, '==', value))
          const snapFallback = await getDocs(qFallback)
          appendSnapshot(snapFallback)
          succeeded = true
        } catch (error) {
          console.warn(`[BaseCharacterService:getPublicCharacters] ${label} fallback query failed`, {
            field,
            value,
            code: error?.code,
            message: error?.message
          })
        }
      }

      return succeeded
    }

    for (const plan of [
      { field: 'permissions', label: 'top-level permissions' },
      { field: 'frontmatter.permissions', label: 'legacy frontmatter permissions' }
    ]) {
      try {
        const qPublic = query(charactersRef, where(plan.field, 'in', PUBLIC_CHARACTER_PERMISSION_CASES))
        const snapshot = await getDocs(qPublic)
        appendSnapshot(snapshot)
      } catch (error) {
        console.warn(
          `[BaseCharacterService:getPublicCharacters] ${plan.label} query failed; trying exact-match fallbacks`,
          { field: plan.field, code: error?.code, message: error?.message }
        )

        const recovered = await runLegacyFallbackQueries(plan.field, plan.label)
        if (!recovered) {
          planErrors.push(error)
        }
      }
    }

    if (!res.length && planErrors.length > 0) {
      throw planErrors[0]
    }

    return res.sort(comparePublicCharacterRecords)
  })
}

/**
 * Base Character Service - Contains version-agnostic functionality
 * Version-specific implementations should extend this class
 */
export class BaseCharacterService {
  constructor() {
    this.gameVersion = null // To be set by subclasses
  }

  /**
   * Set the game version for API calls
   * @param {string} gameVersion
   */
  setGameVersion(gameVersion) {
    this.gameVersion = gameVersion
  }

  /**
   * Check if current user is an admin
   * @returns {Promise<boolean>} - True if user is admin
   */
  async isUserAdmin() {
    const userStore = useUserStore()
    return userStore.isAdmin
  }

  /**
   * Check if current user is a DM
   * @returns {Promise<boolean>} - True if user is DM
   */
  async isUserDM() {
    const userStore = useUserStore()
    return await userStore.checkUserIsDM()
  }

  /**
   * Check if current user can access a character (owner, admin, or DM of campaign)
   * @param {string} characterId - Character ID to check
   * @returns {Promise<boolean>} - True if user has access
   */
  async canAccessCharacter(characterId) {
    try {
      const userStore = useUserStore()
      const userId = userStore.userId

      // Admin has access to all characters
      if (await this.isUserAdmin()) return true

      // Get character data
      const characterDoc = await getDoc(doc(db, 'characters', characterId))
      if (!characterDoc.exists()) return false

      const character = characterDoc.data()

      // Allow PUBLIC visibility for anyone (including anonymous)
      try {
        const rawPerm = character?.permissions || character?.frontmatter?.permissions || 'PRIVATE'
        const perm = String(rawPerm).toUpperCase()
        if (perm === 'PUBLIC') return true
      } catch {}

      // From here, require an authenticated user for non-PUBLIC characters
      if (!userId) return false

      // Owner has access (support ownerId or legacy owner)
      if (character.owner === userId || character.ownerId === userId) return true

      // Any member of the linked campaign can view linked campaign characters.
      try {
        const cid = character.campaignId || character.frontmatter?.campaignId || null
        if (cid) {
          const campaignDoc = await getDoc(doc(db, 'campaigns', cid))
          if (campaignDoc.exists()) {
            const campaignData = campaignDoc.data() || {}
            const isDM = campaignData?.dm === userId
            const isPlayer = !!(
              campaignData?.players &&
              Object.prototype.hasOwnProperty.call(campaignData.players, userId)
            )
            if (isDM || isPlayer) return true
          }
        }
      } catch {}

      // Check if character is shared with user
      if (character.sharedWith && character.sharedWith.includes(userId)) return true

      // USER_SPECIFIC: allow when user is in allowedUserIds (including __all_players__ for campaign players)
      try {
        const allowed = Array.isArray(character.allowedUserIds)
          ? character.allowedUserIds
          : Array.isArray(character.frontmatter?.allowedUserIds)
            ? character.frontmatter.allowedUserIds
            : []
        if (allowed.includes(userId)) return true
        if (allowed.includes('__all_players__')) {
          const cid = character.campaignId || character.frontmatter?.campaignId || null
          if (cid) {
            const campaignDoc = await getDoc(doc(db, 'campaigns', cid))
            if (
              campaignDoc.exists() &&
              campaignDoc.data()?.players &&
              Object.prototype.hasOwnProperty.call(campaignDoc.data().players, userId)
            ) {
              return true
            }
          }
        }
      } catch {}

      // CAMPAIGN: allow campaign members or moderators/DMs
      try {
        const rawPerm = character?.permissions || character?.frontmatter?.permissions || 'PRIVATE'
        const perm = String(rawPerm).toUpperCase()
        if (perm === 'CAMPAIGN' || perm === 'CAMPAIGN_EDIT') {
          const cid = character.campaignId || character.frontmatter?.campaignId || null
          if (cid) {
            const campaignDoc = await getDoc(doc(db, 'campaigns', cid))
            if (campaignDoc.exists()) {
              const c = campaignDoc.data()
              const isDM = c?.dm === userId
              const isPlayer = !!(
                c?.players && Object.prototype.hasOwnProperty.call(c.players, userId)
              )
              if (isDM || isPlayer) return true
            }
          }
        }
      } catch {}

      return false
    } catch (error) {
      console.error('Error checking character access:', error)
      return false
    }
  }

  /**
   * Get all characters for a specific user
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} - Array of character objects
   */
  async getCharacters(userId) {
    if (!userId) {
      console.warn('CharacterService: No userId provided to getCharacters')
      return []
    }

    try {
      // Use retry mechanism for network reliability
      return await withRetry(async () => {
        const charactersRef = collection(db, 'characters')
        const q = query(charactersRef, where('owner', '==', userId))
        const querySnapshot = await getDocs(q)

        return querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
      })
    } catch (error) {
      console.error('Error fetching characters:', error)

      // Handle specific WebChannelConnection errors
      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected, attempting network reset...')
        // Try resetting the network connection
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to load characters')
    }
  }

  /**
   * Get a specific character by ID
   * @param {string} characterId - The character ID
   * @returns {Promise<Object>} - Character object
   */
  async getCharacter(characterId) {
    if (!characterId) {
      throw new Error('Character ID is required')
    }

    try {
      // Check access permissions
      const hasAccess = await this.canAccessCharacter(characterId)
      if (!hasAccess) {
        throw new Error("You don't have permission to access this resource")
      }

      // Use retry mechanism for network reliability
      return await withRetry(async () => {
        const docRef = doc(db, 'characters', characterId)
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
          return {
            id: docSnap.id,
            ...docSnap.data()
          }
        } else {
          throw new Error('Character not found')
        }
      })
    } catch (error) {
      console.error(`Error fetching character ${characterId}:`, error)

      // Handle specific WebChannelConnection errors
      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected, attempting network reset...')
        // Try resetting the network connection
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || `Failed to load character`)
    }
  }

  /**
   * Save a character (create or update)
   * @param {Object} character - Character data
   * @returns {Promise<Object>} - Saved character object with ID
   */
  async saveCharacter(character, schema = null) {
    if (!character) {
      throw new Error('Character data is required')
    }

    // Defensive-copy: ensure we never mutate the caller's object while saving.
    // Use targetedCloneForMutation to cheaply clone expected mutable paths.
    try {
      character = targetedCloneForMutation(character, {
        arrays: ['classes', 'features', 'feats', 'inventory', 'items'],
        objects: ['spells', 'customSpells', 'equipment', 'traits', 'abilities', 'skills']
      })
    } catch {
      // If cloning fails for any reason, continue with the original object to avoid blocking saves.
    }

    // If a schema (key or object) was explicitly provided, ensure indexes are populated according to schema
    // Attempt to load existing character once if we're updating so indexes can be reused
    let existingCharacter = null
    if (character.id) {
      try {
        existingCharacter = await this.getCharacter(character.id)
      } catch {
        existingCharacter = null
      }
    }

    if (schema !== null && schema !== undefined) {
      try {
        // Targeted clone: only deep-clone the array/object paths we expect to mutate.
        // This preserves caller objects while reducing cost compared to a full deep clone.
        const maybeCopy = targetedCloneForMutation(character, {
          arrays: ['classes', 'features', 'feats', 'inventory', 'items'],
          objects: ['spells', 'customSpells', 'equipment', 'traits', 'abilities', 'skills']
        })
        await this._ensureIndexesFromSchema(maybeCopy, schema, { existingCharacter })
        // Ensure we don't accidentally drop important top-level media fields
        try {
          if (
            (maybeCopy.avatar === undefined || maybeCopy.avatar === null) &&
            character &&
            character.avatar !== undefined &&
            character.avatar !== null
          ) {
            maybeCopy.avatar = character.avatar
          }
        } catch {
          /* noop */
        }
        // use the mutated copy for persistence
        character = maybeCopy
      } catch (err) {
        // Do not block save if schema-based indexing fails
        console.warn('Schema-based index population failed:', err && err.message)
      }
    } else {
      // No schema passed explicitly - attempt to infer from character.gameVersion (e.g., '5e' -> 'character:5e')
      try {
        const gv = character && character.gameVersion
        let inferredKey = null
        if (gv) {
          if (typeof gv === 'string' && gv.trim()) {
            inferredKey = gv.startsWith('character:') ? gv : `character:${gv}`
          } else if (typeof gv === 'object' && gv.id) {
            inferredKey =
              typeof gv.id === 'string' && gv.id.trim()
                ? gv.id.startsWith('character:')
                  ? gv.id
                  : `character:${gv.id}`
                : null
          }
        }

        if (inferredKey) {
          // Use targeted clone when inferring schema to avoid mutating the caller
          const maybeCopy = targetedCloneForMutation(character, {
            arrays: ['classes', 'features', 'feats', 'inventory', 'items'],
            objects: ['spells', 'customSpells', 'equipment', 'traits', 'abilities', 'skills']
          })
          await this._ensureIndexesFromSchema(maybeCopy, inferredKey, { existingCharacter })
          // Restore avatar from original if schema processing removed it
          try {
            if (
              (maybeCopy.avatar === undefined || maybeCopy.avatar === null) &&
              character &&
              character.avatar !== undefined &&
              character.avatar !== null
            ) {
              maybeCopy.avatar = character.avatar
            }
          } catch {
            /* noop */
          }
          character = maybeCopy
        }
      } catch (err) {
        // Inference failed - do not block save
        console.warn('Schema inference/index population failed:', err && err.message)
      }
    }

    try {
      // If character has an ID, check access permissions (except for new characters)
      if (character.id) {
        const hasAccess = await this.canAccessCharacter(character.id)
        if (!hasAccess) {
          throw new Error("You don't have permission to edit this character")
        }
      }

      // Get user ID from store
      const userStore = useUserStore()
      const userId = userStore.userId

      if (!userId) {
        throw new Error('User must be authenticated to save characters')
      }

      // Process avatar image if it's a new base64 image
      let skipAvatarPersist = false
      if (character.avatar && character.avatar.startsWith('data:image')) {
        try {
          // If this is an existing character with an ID, delete old avatar first
          if (character.id) {
            try {
              console.log('[BaseCharacterService] Deleting old avatar for character:', character.id)
              await MediaService.deleteCharacterAvatar(character.id)
            } catch (error) {
              console.warn('[BaseCharacterService] Failed to delete old avatar:', error)
              // Continue with upload even if old avatar deletion fails
            }
          }

          // Convert data URL to blob
          const imageBuffer = await this.dataURLtoBlob(character.avatar)

          // Create a File object from the blob
          const avatarFile = new File([imageBuffer], `avatar_${Date.now()}.jpg`, {
            type: 'image/jpeg'
          })

          // Use MediaService to handle the upload (it will delete old avatars)
          // For new characters without an ID, use a temporary ID that will be updated after creation
          const characterId = character.id || 'temp_' + Date.now()
          const ext = (
            avatarFile.name?.split('.').pop() ||
            avatarFile.type.split('/')[1] ||
            'jpg'
          ).toLowerCase()
          const storagePath = `characters/${characterId}/avatar/${uuidv4()}.${ext}`
          try {
            // Diagnostic: log what we're uploading
            try {
              console.log(
                '[BaseCharacterService] Uploading avatar to storagePath:',
                storagePath,
                'fileSize=',
                avatarFile.size
              )
            } catch {
              /* silent */
            }
          } catch {
            /* silent */
          }
          const res = await MediaService.uploadManaged({
            storagePath,
            file: avatarFile,
            userId,
            resourceType: 'character-avatar',
            makePublic: true,
            deleteAfterSuccess: { folderExcept: { folderPath: `characters/${characterId}/avatar` } }
          })
          // Diagnostic: log upload result shape so we can detect string vs object and failures
          try {
            try {
              console.log('[BaseCharacterService] Avatar upload result (type):', typeof res)
            } catch {
              /* silent */
            }
            try {
              console.log(
                '[BaseCharacterService] Avatar upload result (json):',
                JSON.stringify(res)
              )
            } catch {
              console.log('[BaseCharacterService] Avatar upload result (raw):', res)
            }
          } catch {
            /* ignore logging failures */
          }
          character.avatar =
            (typeof res === 'string'
              ? res
              : res && res.url
                ? res.url
                : res && res.path
                  ? res.path
                  : null) || character.avatar
        } catch (error) {
          console.error('Error processing avatar image:', error)
          try {
            console.error('[BaseCharacterService] Avatar upload error details:', {
              code: error?.code,
              message: error?.message
            })
          } catch {
            /* silent */
          }
          // If we were updating an existing character and it had a previous avatar, restore it so we don't remove it
          try {
            if (existingCharacter && existingCharacter.avatar) {
              console.log(
                '[BaseCharacterService] Upload failed; restoring previous avatar from existingCharacter'
              )
              character.avatar = existingCharacter.avatar
              // Allow save to persist the restored URL
              skipAvatarPersist = false
            } else {
              // No previous avatar to restore (new character or none previously) - skip persisting avatar
              skipAvatarPersist = true
            }
          } catch {
            // Fallback: do not persist avatar change
            skipAvatarPersist = true
          }
        }
      }

      // Helper: normalize gameVersion field to canonical id string before persisting
      const normalizeGameVersion = (char) => {
        if (!char) return char
        const copy = { ...char }
        try {
          if (copy.gameVersion && typeof copy.gameVersion === 'object') {
            // Prefer `.id` if available, otherwise leave as-is
            copy.gameVersion = copy.gameVersion.id || copy.gameVersion
          }
        } catch {
          // noop - leave original value
        }
        return copy
      }

      // Use retry mechanism for network reliability
      return await withRetry(async () => {
        if (!character.id) {
          // Create new character
          const sanitized = normalizeGameVersion(character)
          const characterData = sanitizeUndefinedValues({
            ...sanitized,
            // Default permissions: PRIVATE; optional allowedUserIds empty
            permissions: (sanitized && sanitized.permissions) || 'PRIVATE',
            allowedUserIds: Array.isArray(sanitized?.allowedUserIds)
              ? sanitized.allowedUserIds
              : [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          })
          // Avoid saving a data URL or failed avatar change on first create
          if (
            skipAvatarPersist ||
            (typeof characterData.avatar === 'string' && characterData.avatar.startsWith('data:'))
          ) {
            try {
              console.log(
                '[BaseCharacterService] Removing avatar from characterData before create. skipAvatarPersist=',
                skipAvatarPersist,
                'avatar=',
                characterData.avatar
              )
            } catch {
              /* silent */
            }
            delete characterData.avatar
          }

          const docRef = await addDoc(collection(db, 'characters'), characterData)
          await waitForPendingWrites(db) // Wait for write to complete

          // If we used a temporary avatar path, we need to move it to the correct path
          if (character.avatar && character.avatar.includes('temp_')) {
            try {
              // Extract existing avatar URL to get the filename
              const oldAvatarUrl = character.avatar

              // Upload a new avatar with the correct character ID
              const avatarBlob = await fetch(oldAvatarUrl).then((r) => r.blob())
              const avatarFile = new File([avatarBlob], `avatar_${Date.now()}.jpg`, {
                type: 'image/jpeg'
              })

              // Upload with correct character ID
              const ext2 = (
                avatarFile.name?.split('.').pop() ||
                avatarFile.type.split('/')[1] ||
                'jpg'
              ).toLowerCase()
              const storagePath2 = `characters/${docRef.id}/avatar/${uuidv4()}.${ext2}`
              const res = await MediaService.uploadManaged({
                storagePath: storagePath2,
                file: avatarFile,
                userId,
                resourceType: 'character-avatar',
                makePublic: true,
                deleteAfterSuccess: {
                  folderExcept: { folderPath: `characters/${docRef.id}/avatar` }
                }
              })

              // Update character document with new avatar URL
              const newAvatarUrl = res?.url || null
              await updateDoc(doc(db, 'characters', docRef.id), {
                avatar: newAvatarUrl
              })

              // Include the updated avatar URL in the return value
              characterData.avatar = newAvatarUrl

              // --- DELETE THE TEMP AVATAR FILE FROM STORAGE ---
              let tempId = null
              if (oldAvatarUrl.includes('temp_')) {
                // Try to extract the temp ID from the avatar URL
                const tempMatch = oldAvatarUrl.match(/temp_\d+/)
                if (tempMatch) {
                  tempId = tempMatch[0]
                }
              }
              // Try to delete the specific file if possible
              try {
                const url = new URL(oldAvatarUrl)
                const pathMatch = url.pathname.match(/\/o\/(.+)$/)
                if (pathMatch && pathMatch[1]) {
                  const storagePath = decodeURIComponent(pathMatch[1])
                  await MediaService.deleteFile(storagePath)
                }
              } catch {
                // Ignore URL parse errors, fallback to folder delete below
              }
              // Always try to delete the temp avatar folder for the temp ID
              if (tempId) {
                try {
                  await MediaService.deleteCharacterAvatar(tempId)
                } catch (folderDeleteError) {
                  console.warn('Failed to delete temp avatar folder:', folderDeleteError)
                }
              }
              // --- END DELETE ---
            } catch (avatarError) {
              console.error('Error updating avatar after character creation:', avatarError)
              // Continue with the process even if avatar update fails
            }
          }

          const savedCharacter = {
            id: docRef.id,
            ...characterData,
            createdAt: new Date().toISOString(), // Client-side timestamp for immediate UI use
            updatedAt: new Date().toISOString()
          }

          this.clearPublicCharactersCache()
          try {
            await CampaignCharacterPreviewService.syncCharacterPreviews(savedCharacter.id, savedCharacter)
          } catch (syncError) {
            console.warn('[BaseCharacterService] Failed to sync campaign previews after create:', syncError)
          }

          return savedCharacter
        } else {
          // Update existing character
          const characterId = character.id
          const sanitized = normalizeGameVersion(character)
          const characterData = sanitizeUndefinedValues({
            ...sanitized,
            updatedAt: serverTimestamp()
          })

          delete characterData.id // Remove ID from data to be updated
          // Prevent accidental owner changes during updates.
          // Preserve existing owner if present; otherwise avoid writing owner fields.
          const existingOwner =
            existingCharacter && existingCharacter.owner ? existingCharacter.owner : null
          if (existingOwner) {
            characterData.owner = existingOwner
          } else {
            delete characterData.owner
          }
          delete characterData.userId
          delete characterData.createdBy
          // Avoid persisting an invalid avatar change on quota failure or data URL leftovers
          if (
            skipAvatarPersist ||
            (typeof characterData.avatar === 'string' && characterData.avatar.startsWith('data:'))
          ) {
            try {
              console.log(
                '[BaseCharacterService] Removing avatar from characterData before update. skipAvatarPersist=',
                skipAvatarPersist,
                'avatar=',
                characterData.avatar
              )
            } catch {
              /* silent */
            }
            // If we have an existingCharacter with a valid avatar, preserve it
            try {
              if (skipAvatarPersist && existingCharacter && existingCharacter.avatar) {
                characterData.avatar = existingCharacter.avatar
              } else {
                delete characterData.avatar
              }
            } catch {
              delete characterData.avatar
            }
          }

          await updateDoc(doc(db, 'characters', characterId), characterData)
          await waitForPendingWrites(db) // Wait for write to complete

          const mergedCharacter = {
            ...(existingCharacter || {}),
            ...characterData,
            id: characterId,
            owner:
              existingOwner ||
              character.owner ||
              existingCharacter?.owner ||
              existingCharacter?.ownerId ||
              existingCharacter?.userId ||
              existingCharacter?.createdBy ||
              null
          }
          const previousCampaignIds = resolveCharacterCampaignIds(existingCharacter || {})
          const nextCampaignIds = resolveCharacterCampaignIds(mergedCharacter)

          this.clearPublicCharactersCache()
          try {
            await CampaignCharacterPreviewService.syncCharacterPreviews(characterId, mergedCharacter)
            const removedCampaignIds = previousCampaignIds.filter((id) => !nextCampaignIds.includes(id))
            if (removedCampaignIds.length) {
              await CampaignCharacterPreviewService.deleteCharacterPreviews(
                characterId,
                removedCampaignIds
              )
            }
          } catch (syncError) {
            console.warn('[BaseCharacterService] Failed to sync campaign previews after update:', syncError)
          }

          return {
            id: characterId,
            ...characterData,
            updatedAt: new Date().toISOString() // Client-side timestamp for immediate UI use
          }
        }
      })
    } catch (error) {
      console.error('Error saving character:', error)

      // Handle specific WebChannelConnection errors
      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected while saving, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to save character')
    }
  }

  /**
   * Update a character with a partial patch (dot-path keys supported).
   * Intended for concurrent editing to avoid overwriting unrelated fields.
   * @param {string} characterId
   * @param {Object} patch
   * @returns {Promise<Object>}
   */
  async updateCharacterFields(characterId, patch, opts = {}) {
    if (!characterId) {
      throw new Error('Character ID is required')
    }
    if (!patch || typeof patch !== 'object') {
      throw new Error('Patch object is required')
    }

    try {
      const hasAccess = await this.canAccessCharacter(characterId)
      if (!hasAccess) {
        throw new Error("You don't have permission to edit this character")
      }

      const userStore = useUserStore()
      const userId = userStore.userId
      if (!userId) {
        throw new Error('User must be authenticated to save characters')
      }
      const characterRef = doc(db, 'characters', characterId)
      const existingSnapshot = await getDoc(characterRef)
      if (!existingSnapshot.exists()) {
        throw new Error('Character not found')
      }
      const existingCharacter = {
        id: characterId,
        ...existingSnapshot.data()
      }
      const previousCampaignIds = resolveCharacterCampaignIds(existingCharacter)

      const allowOwnerChange = !!opts.allowOwnerChange
      const safePatch = { ...(patch || {}) }
      if (!allowOwnerChange) {
        if (Object.prototype.hasOwnProperty.call(safePatch, 'owner')) delete safePatch.owner
        if (Object.prototype.hasOwnProperty.call(safePatch, 'userId')) delete safePatch.userId
        if (Object.prototype.hasOwnProperty.call(safePatch, 'createdBy')) delete safePatch.createdBy
      }

      const characterPatch = sanitizeUndefinedValues({
        ...safePatch,
        updatedAt: serverTimestamp()
      })

      await updateDoc(characterRef, characterPatch)
      await waitForPendingWrites(db)

      const refreshedSnapshot = await getDoc(characterRef)
      const mergedCharacter = refreshedSnapshot.exists()
        ? {
            id: characterId,
            ...refreshedSnapshot.data()
          }
        : {
            ...existingCharacter,
            ...safePatch,
            id: characterId
          }
      const nextCampaignIds = resolveCharacterCampaignIds(mergedCharacter)

      this.clearPublicCharactersCache()
      try {
        await CampaignCharacterPreviewService.syncCharacterPreviews(characterId, mergedCharacter)
        const removedCampaignIds = previousCampaignIds.filter((id) => !nextCampaignIds.includes(id))
        if (removedCampaignIds.length) {
          await CampaignCharacterPreviewService.deleteCharacterPreviews(characterId, removedCampaignIds)
        }
      } catch (syncError) {
        console.warn('[BaseCharacterService] Failed to sync campaign previews after patch:', syncError)
      }

      return {
        id: characterId,
        ...safePatch,
        updatedAt: new Date().toISOString()
      }
    } catch (error) {
      console.error('Error updating character fields:', error)
      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected while updating, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }
      throw new Error(getFirebaseErrorMessage(error) || 'Failed to save character changes')
    }
  }

  /**
   * Delete a character and all associated resources
   * @param {string} characterId - The character ID
   * @returns {Promise<boolean>} - Success status
   */
  async deleteCharacter(characterId) {
    if (!characterId) {
      throw new Error('Character ID is required')
    }

    try {
      // Handle local characters (they only exist in Pinia store, not in Firestore)
      if (characterId.startsWith('local_')) {
        console.log(`Deleting local character: ${characterId}`)
        // No need to access Firestore for local characters

        // Still try to clean up any associated resources
        try {
          const { useCharacterResourceStore } = await import('../../stores/characterResourceStore')
          const characterResourceStore = useCharacterResourceStore()
          await characterResourceStore.deleteAllCharacterResources(characterId)
        } catch (err) {
          console.error(`Error deleting local character resources:`, err)
        }

        // Local character deleted successfully (the store will handle removal)
        return true
      }

      // For Firebase-stored characters, continue with normal flow
      // Check access permissions
      const hasAccess = await this.canAccessCharacter(characterId)
      if (!hasAccess) {
        throw new Error("You don't have permission to delete this character")
      }

      // Get the character data before deletion to check if it has a campaign
      const characterDoc = await getDoc(doc(db, 'characters', characterId))
      if (!characterDoc.exists()) {
        throw new Error('Character not found')
      }

      const character = characterDoc.data()

      const linkedCampaignIds = resolveCharacterCampaignIds(character)

      // 1. If the character is in campaigns, remove it from them first
      for (const linkedCampaignId of linkedCampaignIds) {
        try {
          await this.removeCharacterFromCampaign(characterId, linkedCampaignId)
        } catch (err) {
          console.error(`Error removing character from campaign before deletion:`, err)
          // Continue with deletion even if removing from campaign fails
        }
      }

      // 2. Delete all character resources (images, gallery, notes, avatar, etc.)
      try {
        // Import dynamically to avoid circular dependency
        const { useCharacterResourceStore } = await import('../../stores/characterResourceStore')
        const characterResourceStore = useCharacterResourceStore()
        await characterResourceStore.deleteAllCharacterResources(characterId)
      } catch (err) {
        console.error(`Error deleting character resources:`, err)
        // Continue with main deletion even if resource cleanup fails
      }

      // 3. Delete avatar images from storage explicitly
      try {
        await MediaService.deleteCharacterAvatar(characterId)
      } catch (err) {
        console.error(`Error deleting character avatar:`, err)
        // Continue with deletion even if avatar deletion fails
      }

      // 4. Delete the character document
      await deleteDoc(doc(db, 'characters', characterId))
      try {
        await CampaignCharacterPreviewService.deleteCharacterPreviews(characterId, linkedCampaignIds)
      } catch (syncError) {
        console.warn('[BaseCharacterService] Failed to delete campaign previews after character delete:', syncError)
      }
      this.clearPublicCharactersCache()
      return true
    } catch (error) {
      console.error(`Error deleting character ${characterId}:`, error)

      // Handle specific WebChannelConnection errors
      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected while deleting, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to delete character')
    }
  }

  /**
   * Get shared characters (characters shared with the user)
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} - Array of shared character objects
   */
  async getSharedCharacters(userId) {
    if (!userId) {
      console.warn('CharacterService: No userId provided to getSharedCharacters')
      return []
    }

    try {
      // Use retry mechanism for network reliability
      return await withRetry(async () => {
        const charactersRef = collection(db, 'characters')
        const q = query(charactersRef, where('sharedWith', 'array-contains', userId))
        const querySnapshot = await getDocs(q)

        return querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
      })
    } catch (error) {
      console.error('Error fetching shared characters:', error)

      // Handle specific WebChannelConnection errors
      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to load shared characters')
    }
  }

  /**
   * Share a character with other users
   * @param {string} characterId - The character ID
   * @param {string[]} userIds - Array of user IDs to share with
   * @returns {Promise<void>}
   */
  async shareCharacter(characterId, userIds) {
    if (!characterId || !userIds || !Array.isArray(userIds)) {
      throw new Error('Character ID and array of user IDs are required')
    }

    try {
      // Use retry mechanism for network reliability
        await withRetry(async () => {
          await updateDoc(doc(db, 'characters', characterId), {
            sharedWith: userIds,
            lastShared: serverTimestamp()
          })
          await waitForPendingWrites(db) // Wait for write to complete
        })
        this.clearPublicCharactersCache()
      } catch (error) {
        console.error(`Error sharing character ${characterId}:`, error)

      // Handle specific WebChannelConnection errors
      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected while sharing, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to share character')
    }
  }

  /**
   * Convert a data URL to a Blob object for Firebase Storage
   * @param {string} dataUrl - The data URL
   * @returns {Promise<Blob>} - Blob object
   */
  async dataURLtoBlob(dataUrl) {
    return fetch(dataUrl).then((res) => res.blob())
  }

  /**
   * Check Firestore connection status
   * @returns {Promise<boolean>} - True if connected
   */
  async checkConnectionStatus() {
    try {
      // Try a simple read operation as a ping test
      const testDocRef = doc(db, '_connectionTest', 'test')
      await getDoc(testDocRef)
      return true
    } catch (error) {
      console.error('Firestore connection test failed:', error)
      return false
    }
  }

  /**
   * Reset Firestore network connection
   * This can help resolve WebChannelConnection errors
   * @returns {Promise<void>}
   */
  async resetConnection() {
    try {
      console.log('Resetting Firestore network connection...')
      await disableNetwork(db)
      await new Promise((resolve) => setTimeout(resolve, 1000)) // Wait a second
      await enableNetwork(db)
      console.log('Firestore network connection reset complete')
    } catch (error) {
      console.error('Failed to reset Firestore connection:', error)
      throw error
    }
  }

  // Campaign-related methods
  /**
   * Get all characters for campaigns where user is DM
   * @returns {Promise<Array>} - Array of character objects
   */
  async getDMCharacters() {
    try {
      const userStore = useUserStore()
      const userId = userStore.userId

      if (!userId) {
        return []
      }

      // Admin can see all characters
      if (await this.isUserAdmin()) {
        return await this.getAllCharacters()
      }

      // Get campaigns where user is DM
      const campaignsRef = collection(db, 'campaigns')
      const campaignQuery = query(campaignsRef, where('dm', '==', userId))
      const campaignSnapshot = await getDocs(campaignQuery)

      if (campaignSnapshot.empty) {
        return []
      }

      // Get all character IDs from these campaigns
      const characterIds = []
      campaignSnapshot.docs.forEach((doc) => {
        const campaign = doc.data()
        if (campaign.characters && Array.isArray(campaign.characters)) {
          characterIds.push(...campaign.characters)
        }
      })

      if (characterIds.length === 0) {
        return []
      }

      // Get character details
      const characters = await Promise.all(
        characterIds.map(async (charId) => {
          try {
            const characterDoc = await getDoc(doc(db, 'characters', charId))
            if (characterDoc.exists()) {
              return {
                id: characterDoc.id,
                ...characterDoc.data()
              }
            }
            return null
          } catch (error) {
            console.error(`Error fetching character ${charId}:`, error)
            return null
          }
        })
      )

      // Filter out any null values (failed fetches)
      return characters.filter((char) => char !== null)
    } catch (error) {
      console.error('Error fetching DM characters:', error)

      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to load DM characters')
    }
  }

  /**
   * Get all characters (admin only)
   * @returns {Promise<Array>} - Array of all character objects
   */
  async getAllCharacters() {
    try {
      // Check if user is admin
      if (!(await this.isUserAdmin())) {
        throw new Error('Unauthorized access. Admin privileges required.')
      }

      // Use retry mechanism for network reliability
      return await withRetry(async () => {
        const charactersRef = collection(db, 'characters')
        const querySnapshot = await getDocs(charactersRef)

        return querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
      })
    } catch (error) {
      console.error('Error fetching all characters:', error)

      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to load all characters')
    }
  }

  /**
   * Get all PUBLIC characters (anonymous-friendly)
   * @returns {Promise<Array>} - Array of public character objects
   */
  getCachedPublicCharacters(options = {}) {
    const entry = readPublicCharactersCacheEntry(options)
    return entry ? entry.data : null
  }

  clearPublicCharactersCache() {
    clearPublicCharactersCacheEntry()
  }

  async getPublicCharacters(options = {}) {
    const { forceRefresh = false, maxAgeMs = PUBLIC_CHARACTERS_CACHE_TTL_MS } = options || {}

    if (!forceRefresh) {
      const cached = this.getCachedPublicCharacters({ maxAgeMs })
      if (cached) return cached
    }

    if (publicCharactersPendingRequest) {
      return await publicCharactersPendingRequest
    }

    try {
      publicCharactersPendingRequest = (async () => {
        try {
          const freshList = await fetchPublicCharactersFromFirestore()
          return writePublicCharactersCache(freshList)
        } finally {
          publicCharactersPendingRequest = null
        }
      })()

      return await publicCharactersPendingRequest
    } catch (error) {
      console.error('Error fetching public characters:', error)
      if (error.message && error.message.includes('WebChannelConnection')) {
        try {
          await disableNetwork(db)
          await enableNetwork(db)
        } catch {}
      }
      throw new Error(getFirebaseErrorMessage(error) || 'Failed to load public characters')
    }
  }

  /**
   * Get characters for a specific campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array>} - Array of character objects
   */
  async getCharactersByCampaign(campaignId) {
    if (!campaignId) {
      throw new Error('Campaign ID is required')
    }

    try {
      // Check if user has access to this campaign
      const userStore = useUserStore()
      const userId = userStore.userId

      if (!userId) {
        throw new Error('User authentication required')
      }

      // Get campaign data
      const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId))
      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      // Check if user is DM or player in campaign or admin
      const isAdmin = await this.isUserAdmin()
      const isDM = campaign.dm === userId
      const isPlayer = hasPlayer(campaign.players, userId)
      const userRole = getPlayerRole(campaign.players, userId)
      const isModerator = userRole === 'moderator'

      if (!isAdmin && !isDM && !isPlayer) {
        throw new Error("You don't have access to this campaign")
      }

      const characterIds = Array.isArray(campaign.characters)
        ? campaign.characters
        : Object.keys(campaign.characters || {})

      if (characterIds.length === 0) {
        return []
      }

      // Get character details
      const characters = await Promise.all(
        characterIds.map(async (charId) => {
          try {
            const characterDoc = await getDoc(doc(db, 'characters', charId))
            if (characterDoc.exists()) {
              return {
                id: characterDoc.id,
                ...characterDoc.data()
              }
            }
            return null
          } catch (error) {
            console.error(`Error fetching character ${charId}:`, error)
            return null
          }
        })
      )

      // Filter out any null values (failed fetches)
      const validCharacters = characters.filter((char) => char !== null)
      await Promise.allSettled(
        validCharacters.map((character) => {
          const characterId = String(character?.id || '').trim()
          if (!characterId) return Promise.resolve()
          return CampaignCharacterPreviewService.upsertCampaignCharacterPreview(
            campaignId,
            characterId,
            character
          )
        })
      )
      return validCharacters
    } catch (error) {
      console.error(`Error fetching characters for campaign ${campaignId}:`, error)

      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to load campaign characters')
    }
  }

  /**
   * Add campaign information to a character
   * @param {string} characterId - Character ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} - Updated character
   */
  async addCharacterToCampaign(characterId, campaignId) {
    if (!characterId || !campaignId) {
      throw new Error('Character ID and Campaign ID are required')
    }

    try {
      // Check if user has access
      const userStore = useUserStore()
      const userId = userStore.userId

      if (!userId) {
        throw new Error('User authentication required')
      }

      // Get campaign data
      const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId))
      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      const isAdmin = await this.isUserAdmin()
      const isDM = campaign.dm === userId
      const isPlayer = hasPlayer(campaign.players, userId)

      if (!isAdmin && !isDM && !isPlayer) {
        throw new Error('Only the DM, an admin, or a campaign player can add characters')
      }

      const characterRef = doc(db, 'characters', characterId)
      const characterDoc = await getDoc(characterRef)
      if (!characterDoc.exists()) {
        throw new Error('Character not found')
      }

      const characterData = characterDoc.data()
      const ownerId = characterData.owner || characterData.userId || characterData.createdBy || null
      const nextCampaignIds = Array.from(
        new Set([...resolveCharacterCampaignIds(characterData), String(campaignId)])
      )

      if (!isAdmin && !isDM && ownerId !== userId) {
        throw new Error('You can only add characters you own to this campaign')
      }

      const toCharacterMap = (value) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return { ...value }
        }
        if (Array.isArray(value)) {
          return value.reduce((acc, id) => {
            if (!id) return acc
            acc[id] = { id }
            return acc
          }, {})
        }
        return {}
      }

      const updatedCharacters = toCharacterMap(campaign.characters)

      if (Object.prototype.hasOwnProperty.call(updatedCharacters, characterId)) {
        this.clearPublicCharactersCache()
        return await this.getCharacter(characterId)
      }

      updatedCharacters[characterId] = {
        id: characterId,
        owner: ownerId,
        ...buildCharacterCampaignPreview(characterData),
        addedBy: userId,
        addedAt: new Date().toISOString()
      }

      const batch = writeBatch(db)
      batch.update(doc(db, 'campaigns', campaignId), {
        characters: updatedCharacters,
        updated: serverTimestamp()
      })
      batch.update(characterRef, {
        // Track the most recently joined campaign for backwards compatibility,
        // while maintaining all linked campaigns for preview sync.
        campaignId,
        campaignIds: nextCampaignIds,
        updated: serverTimestamp()
      })
      batch.set(
        getCampaignCharacterPreviewDocRef(campaignId, characterId),
        buildCampaignCharacterPreviewDoc(characterId, {
          ...characterData,
          owner: ownerId,
          campaignId,
          campaignIds: nextCampaignIds
        }),
        { merge: true }
      )
      await batch.commit()

      this.clearPublicCharactersCache()
      return await this.getCharacter(characterId)
    } catch (error) {
      console.error(`Error adding character ${characterId} to campaign ${campaignId}:`, error)

      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to add character to campaign')
    }
  }

  /**
   * Remove campaign information from a character
   * @param {string} characterId - Character ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} - Updated character
   */
  async removeCharacterFromCampaign(characterId, campaignId) {
    if (!characterId || !campaignId) {
      throw new Error('Character ID and Campaign ID are required')
    }

    try {
      // Check if user has access
      const userStore = useUserStore()
      const userId = userStore.userId

      if (!userId) {
        throw new Error('User authentication required')
      }

      // Get campaign data
      const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId))
      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      const isAdmin = await this.isUserAdmin()
      const isDM = campaign.dm === userId
      const userRole = getPlayerRole(campaign.players, userId)
      const isModerator = userRole === 'moderator'

      const toCharacterMap = (value) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return { ...value }
        }
        if (Array.isArray(value)) {
          return value.reduce((acc, id) => {
            if (!id) return acc
            acc[id] = { id }
            return acc
          }, {})
        }
        return {}
      }

      const updatedCharacters = toCharacterMap(campaign.characters)
      if (!Object.prototype.hasOwnProperty.call(updatedCharacters, characterId)) {
        throw new Error('Character not in campaign')
      }

      const entryMeta = updatedCharacters[characterId]
      const entryOwnerId =
        entryMeta?.owner || entryMeta?.ownerId || entryMeta?.userId || entryMeta?.createdBy || null
      const characterRef = doc(db, 'characters', characterId)
      let characterExists = false
      let characterData = null
      try {
        const characterDoc = await getDoc(characterRef)
        if (characterDoc.exists()) {
          characterExists = true
          characterData = characterDoc.data()
        }
      } catch (readError) {
        const code = String(readError?.code || '')
        const message = String(readError?.message || '').toLowerCase()
        const isPermissionError = code.includes('permission') || message.includes('permission')
        if (!isPermissionError) {
          throw readError
        }
      }

      const ownerId =
        characterData?.owner ||
        characterData?.userId ||
        characterData?.createdBy ||
        entryOwnerId ||
        null
      const remainingCampaignIds = characterExists
        ? resolveCharacterCampaignIds(characterData).filter((id) => id !== String(campaignId))
        : []
      const addedByUser = entryMeta?.addedBy === userId

      if (!isAdmin && !isDM && ownerId !== userId && !(isModerator && addedByUser)) {
        throw new Error(
          'Only the DM, an admin, the owner, or a moderator who added it can remove this character'
        )
      }

      delete updatedCharacters[characterId]

      const batch = writeBatch(db)
      if (characterExists && characterData) {
        batch.update(characterRef, {
          // Only clear the primary campaignId if it currently points at this campaign.
          campaignId:
            characterData.campaignId === campaignId
              ? remainingCampaignIds[0] || null
              : characterData.campaignId,
          campaignIds: remainingCampaignIds,
          updated: serverTimestamp()
        })
      }
      batch.update(doc(db, 'campaigns', campaignId), {
        characters: updatedCharacters,
        updated: serverTimestamp()
      })
      batch.delete(getCampaignCharacterPreviewDocRef(campaignId, characterId))
      await batch.commit()

      this.clearPublicCharactersCache()
      if (!characterExists) {
        return {
          id: characterId,
          campaignId: null,
          campaignIds: []
        }
      }

      // Return updated character
      return await this.getCharacter(characterId)
    } catch (error) {
      console.error(`Error removing character ${characterId} from campaign ${campaignId}:`, error)

      if (error.message && error.message.includes('WebChannelConnection')) {
        console.warn('Connection error detected, attempting network reset...')
        await disableNetwork(db)
        await enableNetwork(db)
      }

      throw new Error(getFirebaseErrorMessage(error) || 'Failed to remove character from campaign')
    }
  }

  // Utility methods
  /**
   * Get a character card preview object with minimal data
   * @param {Object} character Complete character object
   * @returns {Object} Minimal character card data
   */
  getCharacterCardData(character) {
    if (!character) return null

    return {
      id: character.id,
      name: character.name,
      race: character.race,
      level: character.level,
      classes: character.classes
        ? character.classes.map((c) => `${c.name} ${c.level}`).join(', ')
        : '',
      avatar: character.avatar,
      thumbnail: character.thumbnail || character.avatar, // Use avatar as fallback
      updatedAt: character.updatedAt
    }
  }

  /**
   * Set or update a character's thumbnail
   * @param {Object} character Character object
   * @param {string} imageUrl URL of the thumbnail image
   * @returns {Object} Updated character
   */
  setCharacterThumbnail(character, imageUrl) {
    if (!character) return null

    character.thumbnail = imageUrl
    return character
  }

  /**
   * Generate a thumbnail from a larger image
   * @param {string} imageUrl URL of the original image
   * @returns {Promise<string>} URL of the thumbnail image (data URL)
   */
  async generateThumbnail(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'Anonymous'

      img.onload = () => {
        // Create a small canvas for the thumbnail
        const canvas = document.createElement('canvas')
        const maxSize = 100 // Thumbnail size
        const scale = Math.min(maxSize / img.width, maxSize / img.height)

        canvas.width = img.width * scale
        canvas.height = img.height * scale

        // Draw the image at the smaller size
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

        // Get the data URL and resolve
        resolve(canvas.toDataURL('image/jpeg', 0.8))
      }

      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }

      img.src = imageUrl
    })
  }

  /**
   * Export character to JSON file
   * @param {Object} character Character object to export
   * @returns {string} JSON data as string
   */
  exportCharacter(character) {
    try {
      return JSON.stringify(character, null, 2)
    } catch (error) {
      console.error('Error exporting character:', error)
      throw new Error('Failed to export character')
    }
  }

  /**
   * Import character from JSON data
   * @param {string} jsonData JSON character data
   * @returns {Object} Imported character
   */
  importCharacter(jsonData) {
    try {
      const character = JSON.parse(jsonData)
      // Validate character has at least an id and name
      if (!character || !character.name) {
        throw new Error('Invalid character data')
      }

      // Generate new ID to avoid duplicates
      character.id = 'imported_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      character.importedAt = new Date().toISOString()

      return character
    } catch (error) {
      console.error('Error importing character:', error)
      throw new Error('Failed to import character')
    }
  }

  // Abstract methods to be implemented by subclasses
  /**
   * Get empty character template - must be implemented by subclasses
   * @returns {Object} Empty character object
   */
  getEmptyCharacter() {
    throw new Error('getEmptyCharacter must be implemented by subclasses')
  }

  /**
   * Calculate proficiency bonus - may be overridden by subclasses
   * @param {number} level Character level
   * @returns {number} Proficiency bonus
   */
  calculateProficiencyBonus(level) {
    // Default implementation - can be overridden
    return Math.floor((level - 1) / 4) + 2
  }

  /**
   * Calculate ability score modifier - may be overridden by subclasses
   * @param {number} score Ability score
   * @returns {string} Formatted ability modifier
   */
  getAbilityModifier(score) {
    // Default implementation - can be overridden
    const mod = Math.floor((score - 10) / 2)
    return mod >= 0 ? `+${mod}` : `${mod}`
  }

  /**
   * Get ability modifier value (not formatted) - may be overridden by subclasses
   * @param {number} score Ability score
   * @returns {number} Modifier value
   */
  getAbilityModifierValue(score) {
    // Default implementation - can be overridden
    return Math.floor((score - 10) / 2)
  }

  /**
   * Schema-driven async index population helper.
   * Loads the provided schema key and finds array paths where item schemas include an `index` property,
   * then ensures every item at those paths has an `index` (slug/id-generated). This mutates the provided character object.
   * Note: This method intentionally does NOT provide fallback heuristics — callers should handle fallbacks if desired.
   * @param {Object} char Character object to mutate
   * @param {string} schemaKey Key used by SchemaService to load schema (e.g., 'character:5e')
   */
  async _ensureIndexesFromSchema(char, schemaKeyOrSchema, opts = {}) {
    if (!char || typeof char !== 'object') return

    // Accept either a schema key string or an already-loaded schema object
    let schema = null
    if (schemaKeyOrSchema && typeof schemaKeyOrSchema === 'object') {
      schema = schemaKeyOrSchema
    } else {
      const SchemaService = (await import('@/services/SchemaService')).default
      schema = await SchemaService.getSchema(schemaKeyOrSchema)
    }

    if (!schema || typeof schema !== 'object') throw new Error('Schema not found')

    // Simple $ref resolver for local #/definitions/... references
    const resolveRef = (node) => {
      if (!node) return node
      if (node.$ref && typeof node.$ref === 'string' && node.$ref.startsWith('#/definitions/')) {
        const defKey = node.$ref.replace('#/definitions/', '')
        return schema.definitions && schema.definitions[defKey] ? schema.definitions[defKey] : node
      }
      return node
    }

    // indexedPaths: array of { path: [...], itemsSchema }
    const indexedPaths = []

    const collect = (node, path = []) => {
      if (!node || typeof node !== 'object') return
      if (node.type === 'array' && node.items) {
        const items = resolveRef(node.items)
        if (
          items &&
          items.properties &&
          Object.prototype.hasOwnProperty.call(items.properties, 'index')
        ) {
          indexedPaths.push({ path: [...path], itemsSchema: items })
          return
        }
      }

      if (node.type === 'object' && node.properties) {
        for (const p of Object.keys(node.properties)) {
          collect(resolveRef(node.properties[p]), path.concat(p))
        }
      }
    }

    collect(schema)

    // helper to generate index preferring a slugified name
    const makeIndexFrom = (item) => {
      if (!item) return null
      if (item.name) {
        return String(item.name)
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
      }
      if (item.index) return item.index
      if (item.id) return String(item.id)
      return `idx_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
    }

    const ensureArrayAtPath = (root, path, itemsSchema, existingArray) => {
      if (!root || !path || path.length === 0) return
      let cur = root
      for (let i = 0; i < path.length; i++) {
        const seg = path[i]
        if (cur[seg] === undefined || cur[seg] === null) return
        cur = cur[seg]
      }
      if (!Array.isArray(cur)) return

      const allowsId =
        itemsSchema &&
        itemsSchema.properties &&
        Object.prototype.hasOwnProperty.call(itemsSchema.properties, 'id')

      // Build a set/map of already-used indexes in this array
      const used = new Set()
      for (const it of cur) {
        if (
          it &&
          typeof it === 'object' &&
          it.index !== undefined &&
          it.index !== null &&
          it.index !== ''
        ) {
          used.add(String(it.index))
        }
      }

      // Helper to pick a unique candidate based on baseSlug
      const pickUnique = (base) => {
        base = String(base)
        if (!used.has(base)) {
          used.add(base)
          return base
        }
        // find next suffix
        let i = 2
        while (true) {
          const candidate = `${base}-${i}`
          if (!used.has(candidate)) {
            used.add(candidate)
            return candidate
          }
          i++
        }
      }

      // Prepare existing lookup maps from existingArray if provided
      const existingById = new Map()
      const existingByName = new Map()
      if (Array.isArray(existingArray)) {
        for (const e of existingArray) {
          if (e && typeof e === 'object' && e.index) {
            if (e.id !== undefined && e.id !== null) existingById.set(String(e.id), String(e.index))
            if (e.name) existingByName.set(String(e.name).toLowerCase(), String(e.index))
          }
        }
      }

      for (const it of cur) {
        if (it && typeof it === 'object') {
          if (it.index === undefined || it.index === null || it.index === '') {
            // Try to reuse an existing index by id or name
            let reused = null
            if (it.id !== undefined && it.id !== null && existingById.has(String(it.id))) {
              const candidate = existingById.get(String(it.id))
              if (!used.has(candidate)) {
                reused = candidate
                used.add(candidate)
              }
            }
            if (!reused && it.name && existingByName.has(String(it.name).toLowerCase())) {
              const candidate = existingByName.get(String(it.name).toLowerCase())
              if (!used.has(candidate)) {
                reused = candidate
                used.add(candidate)
              }
            }

            if (reused) {
              it.index = reused
            } else {
              const base = makeIndexFrom(it)
              const unique = base
                ? pickUnique(base)
                : pickUnique(`idx_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`)
              it.index = unique
            }
          }
          // Remove id if schema does not allow it
          if (!allowsId && Object.prototype.hasOwnProperty.call(it, 'id')) {
            delete it.id
          }
        }
      }
    }

    // Apply to each discovered path; provide matching existing array slice when available for index reuse
    for (const entry of indexedPaths) {
      let existingArray = null
      try {
        if (opts && opts.existingCharacter && Array.isArray(opts.existingCharacter)) {
          // existingCharacter is sometimes an array (if whole character was passed incorrectly); support both
          existingArray = opts.existingCharacter
        } else if (opts && opts.existingCharacter && typeof opts.existingCharacter === 'object') {
          // Walk the existingCharacter to reach the same path
          let curEx = opts.existingCharacter
          for (const seg of entry.path) {
            if (!curEx || typeof curEx !== 'object') {
              curEx = null
              break
            }
            curEx = curEx[seg]
          }
          existingArray = Array.isArray(curEx) ? curEx : null
        }
      } catch {
        existingArray = null
      }

      ensureArrayAtPath(char, entry.path, entry.itemsSchema, existingArray)
    }
  }
}
